import React, { useState } from "react";
import { useLocation } from "wouter";
import { AppShell } from "@/components/layout/app-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Building2, PlusCircle, ArrowRight, FileText, Calendar, 
  Search, Plus, Sparkles, CheckCircle2, Clock, Layers, ArrowUpRight 
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useCustomer } from "@/hooks/use-customer";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export default function HomePage() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { customers, isLoading, setActiveCustomerId, activeCustomerId } = useCustomer();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState("");
  const [isAddFacilityOpen, setIsAddFacilityOpen] = useState(false);
  const [newFacilityName, setNewFacilityName] = useState("");

  // Query all proposals
  const { data: proposals = [], isLoading: proposalsLoading } = useQuery<any[]>({
    queryKey: ["/api/osem"],
    enabled: !!user,
  });
  const proposalsList = Array.isArray(proposals) ? proposals : [];

  // Create facility mutation
  const createFacilityMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", "/api/facilities", { username: name });
      return res.json();
    },
    onSuccess: (newFacility) => {
      queryClient.invalidateQueries({ queryKey: ["/api/facilities"] });
      toast({
        title: "Facility Created",
        description: `Successfully added ${newFacility.username || newFacility.name}.`,
      });
      setIsAddFacilityOpen(false);
      setNewFacilityName("");
    },
    onError: (err: any) => {
      toast({
        title: "Failed to create facility",
        description: err.message || "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  });

  const handleCreateFacilitySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFacilityName.trim()) return;
    createFacilityMutation.mutate(newFacilityName.trim());
  };

  const getFacilityProposals = (customer: any) => {
    const custId = customer.id?.toString();
    const custName = customer.username?.toLowerCase() || "";
    
    return proposalsList.filter(p => {
      const pCustId = p.customerId?.toString();
      const pBuildId = p.buildingId?.toString();
      const pCustName = p.customerName?.toLowerCase() || "";
      const pCompName = p.companyName?.toLowerCase() || "";
      
      if (custId && (pCustId === custId || pBuildId === custId)) return true;
      if (custName && (pCustName === custName || pCompName.includes(custName))) return true;
      if (p.facilities && Array.isArray(p.facilities)) {
        return p.facilities.some((f: any) => f.name?.toLowerCase()?.includes(custName));
      }
      return false;
    });
  };

  const filteredCustomers = (customers || []).filter(c => {
    const matchesName = c.username.toLowerCase().includes(searchQuery.toLowerCase());
    const facilityProps = getFacilityProposals(c);
    const matchesProposal = facilityProps.some(p => p.companyName.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesName || matchesProposal;
  });

  // Collect proposals not linked to any facility
  const unassignedProposals = proposalsList.filter(p => {
    return !(customers || []).some(c => {
      const custId = c.id?.toString();
      const custName = c.username?.toLowerCase() || "";
      if (custId && (p.customerId?.toString() === custId || p.buildingId?.toString() === custId)) return true;
      if (custName && (p.customerName?.toLowerCase() === custName || p.companyName?.toLowerCase()?.includes(custName))) return true;
      return false;
    });
  });

  return (
    <AppShell>
      <div className="space-y-6 pb-12">
        {/* Top Hub Banner */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-5">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2.5">
              <Building2 className="h-8 w-8 text-primary" />
              Energy Intelligence Facilities & Proposals
            </h1>
            <p className="text-muted-foreground mt-1">
              Select an enterprise facility to manage its digital twin, calibrate energy models, and manage NYSERDA OsEM proposals.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              onClick={() => setIsAddFacilityOpen(true)}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Facility
            </Button>

            <Button 
              onClick={() => setLocation("/osem-onboarding/new")}
              className="gap-2 bg-primary hover:bg-primary/90"
            >
              <PlusCircle className="h-4 w-4" />
              New Proposal
            </Button>
          </div>
        </div>

        {/* Search & Statistics Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-muted/40 p-3.5 rounded-xl border">
          <div className="relative flex-1 w-full">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-2.5" />
            <Input 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search facilities or proposals by name..."
              className="pl-9 bg-background h-9 text-xs"
            />
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground whitespace-nowrap">
            <Badge variant="outline" className="font-mono">
              {filteredCustomers.length} {filteredCustomers.length === 1 ? "Facility" : "Facilities"}
            </Badge>
            <Badge variant="secondary" className="font-mono">
              {proposalsList.length} Total {proposalsList.length === 1 ? "Proposal" : "Proposals"}
            </Badge>
          </div>
        </div>

        {/* Facilities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {isLoading ? (
            <div className="col-span-full py-16 text-center text-muted-foreground">
              <p>Loading enterprise facilities...</p>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="col-span-full text-center py-16 border rounded-xl bg-muted/20">
              <Building2 className="mx-auto h-12 w-12 text-muted-foreground mb-3 opacity-60" />
              <h3 className="text-lg font-semibold mb-1">No Facilities Found</h3>
              <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
                {searchQuery ? "No facilities match your search query." : "No facilities have been registered yet."}
              </p>
              <Button onClick={() => setIsAddFacilityOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" /> Add First Facility
              </Button>
            </div>
          ) : (
            filteredCustomers.map((customer) => {
              const facilityProposals = getFacilityProposals(customer);
              const isCurrentActive = activeCustomerId === customer.id.toString();

              return (
                <Card 
                  key={customer.id} 
                  className={`flex flex-col transition-all duration-200 hover:shadow-md ${
                    isCurrentActive ? "border-primary ring-1 ring-primary/30 bg-primary/[0.02]" : "hover:border-primary/40"
                  }`}
                >
                  <CardHeader className="pb-3 border-b bg-muted/20">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="h-4 w-4 text-primary shrink-0" />
                          <CardTitle className="text-lg font-bold truncate" title={customer.username}>
                            {customer.username}
                          </CardTitle>
                        </div>
                        <CardDescription className="text-xs">
                          Enterprise Monitored Facility
                        </CardDescription>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        <Badge 
                          variant={facilityProposals.length > 0 ? "default" : "outline"} 
                          className="text-xs font-mono"
                        >
                          {facilityProposals.length} {facilityProposals.length === 1 ? "Proposal" : "Proposals"}
                        </Badge>
                        {isCurrentActive && (
                          <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 text-[10px] font-bold">
                            Active Scope
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="flex-1 py-4 space-y-3">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                      <span>Associated OsEM Proposals</span>
                      <span className="text-[11px] font-mono lowercase">{facilityProposals.length} attached</span>
                    </div>

                    {facilityProposals.length === 0 ? (
                      <div className="text-center py-6 px-4 rounded-lg border border-dashed bg-muted/10">
                        <FileText className="h-6 w-6 text-muted-foreground/60 mx-auto mb-1.5" />
                        <p className="text-xs text-muted-foreground mb-3">No onboarding proposals created for this facility yet.</p>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setActiveCustomerId(customer.id.toString());
                            setLocation("/osem-onboarding/new");
                          }}
                          className="h-7 text-xs gap-1.5"
                        >
                          <Plus className="h-3.5 w-3.5" /> Create Proposal
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2.5">
                        {facilityProposals.map((proposal) => {
                          const isCompleted = proposal.status === "completed" || proposal.status === "approved";
                          return (
                            <div 
                              key={proposal.id}
                              className="p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge 
                                    variant={isCompleted ? "default" : "secondary"}
                                    className="text-[10px] px-1.5 py-0 h-4 font-semibold capitalize"
                                  >
                                    {proposal.status || "draft"}
                                  </Badge>
                                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 font-mono">
                                    v{proposal.version || 1}
                                  </Badge>
                                  <span className="text-[11px] text-muted-foreground flex items-center gap-1 ml-auto sm:ml-0 font-mono">
                                    <Calendar className="h-3 w-3" />
                                    {proposal.createdAt ? format(new Date(proposal.createdAt), "MMM d, yyyy") : "Recent"}
                                  </span>
                                </div>
                                <h4 className="text-xs font-bold text-foreground truncate" title={proposal.companyName}>
                                  {proposal.companyName}
                                </h4>
                              </div>

                              <div className="flex items-center gap-1.5 shrink-0">
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setActiveCustomerId(customer.id.toString());
                                    setLocation(`/osem-onboarding/${proposal.id}`);
                                  }}
                                  className="h-7 text-xs px-2.5 gap-1"
                                >
                                  <FileText className="h-3 w-3 text-primary" />
                                  <span>View Proposal</span>
                                </Button>

                                {isCompleted && (
                                  <Button 
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => {
                                      setActiveCustomerId(customer.id.toString());
                                      setLocation(`/osem-onboarding/${proposal.id}/emp`);
                                    }}
                                    className="h-7 text-xs px-2.5 gap-1 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20"
                                  >
                                    <Sparkles className="h-3 w-3" />
                                    <span>EMP Plan</span>
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>

                  <CardFooter className="pt-3 pb-3 border-t bg-muted/10 flex items-center justify-between gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-xs h-8 gap-1.5 text-primary hover:text-primary hover:bg-primary/10"
                      onClick={() => {
                        setActiveCustomerId(customer.id.toString());
                        setLocation("/osem-onboarding/new");
                      }}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      <span>New Proposal for this Facility</span>
                    </Button>

                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-xs h-8 gap-1.5"
                      onClick={() => {
                        setActiveCustomerId(customer.id.toString());
                        setLocation(`/osem-proposals`);
                      }}
                    >
                      <span>Manage All ({facilityProposals.length})</span>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </CardFooter>
                </Card>
              );
            })
          )}
        </div>

        {/* Unassigned Proposals Section (if any exist) */}
        {unassignedProposals.length > 0 && (
          <div className="mt-8 border rounded-xl p-5 bg-muted/20">
            <h3 className="text-base font-bold mb-1 flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              Unassigned & General Proposals ({unassignedProposals.length})
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              These proposals are not yet directly attached to a specific registered facility.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {unassignedProposals.map((proposal) => (
                <div 
                  key={proposal.id}
                  className="p-3 bg-card border rounded-lg hover:border-primary/50 transition-colors flex items-center justify-between"
                >
                  <div className="min-w-0 pr-2">
                    <h4 className="text-xs font-bold truncate">{proposal.companyName}</h4>
                    <p className="text-[11px] text-muted-foreground">v{proposal.version || 1} • {proposal.status || "draft"}</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="h-7 text-xs shrink-0"
                    onClick={() => setLocation(`/osem-onboarding/${proposal.id}`)}
                  >
                    Open <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Facility Dialog */}
      <Dialog open={isAddFacilityOpen} onOpenChange={setIsAddFacilityOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Add New Enterprise Facility
            </DialogTitle>
            <DialogDescription className="text-xs">
              Register a new facility or building to associate digital twins and OsEM onboarding proposals.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateFacilitySubmit} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Facility Name / Business Title</Label>
              <Input 
                value={newFacilityName}
                onChange={(e) => setNewFacilityName(e.target.value)}
                placeholder="e.g. Russo Distribution Hub"
                required
                className="text-xs h-9"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => setIsAddFacilityOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                size="sm"
                disabled={createFacilityMutation.isPending || !newFacilityName.trim()}
              >
                {createFacilityMutation.isPending ? "Creating..." : "Save Facility"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
